import React from "react";
import { Button, Card, CardContent, Typography } from "@material-ui/core";
import { t } from "adapters/i18n";
import { BannerAlert } from "features/shared/components";
import type {
  CycleValidationError,
  EditableCyclePeriod,
  ForageCycleRecord,
} from "../../../forageCycleManagementUtils";
import ForageCyclePeriodRow, {
  type EditablePeriodField,
} from "./ForageCyclePeriodRow";

interface ForageCycleCardProps {
  cycle: ForageCycleRecord;
  cycleTestId: string;
  periods: EditableCyclePeriod[];
  periodsError: string;
  saving: boolean;
  serverFieldErrors: Array<{ fieldLabel: string; message: string }>;
  validationErrors: CycleValidationError[];
  onAddPeriod: () => void;
  onChangePeriod: (
    index: number,
    field: EditablePeriodField,
    value: string,
  ) => void;
  onRemovePeriod: (index: number) => void;
}

function ForageCycleCard({
  cycle,
  cycleTestId,
  periods,
  periodsError,
  saving,
  serverFieldErrors,
  validationErrors,
  onAddPeriod,
  onChangePeriod,
  onRemovePeriod,
}: ForageCycleCardProps) {
  const isLegacy = periods.length > 4;
  const pastureCount = cycle.quantidadePastosAtivos || 0;
  const pastureCountLabel = t(
    pastureCount === 1
      ? "forageCycle:cycle-active-pasture-count-one"
      : "forageCycle:cycle-active-pasture-count",
    { count: pastureCount },
  );
  const forageName =
    cycle.nomeForragem ||
    t("forageCycle:cycle-forage-id", { id: cycle.IdForragem });
  const rowErrors = validationErrors.filter(
    (error) => error.field !== "periods",
  );

  return (
    <Card
      className="forage-cycle-card"
      component="article"
      elevation={5}
      data-testid={cycleTestId}
    >
      <div className="forage-cycle-card__header">
        <Typography component="h3" className="forage-cycle-card__title">
          {forageName}
        </Typography>
        <span className="forage-cycle-card__count">{pastureCountLabel}</span>
      </div>

      <CardContent className="forage-cycle-card__content">
        {serverFieldErrors.length > 0 && (
          <div
            id={`server-validation-${cycleTestId}`}
            role="alert"
            aria-live="polite"
          >
            <BannerAlert
              type="error"
              className="forage-cycle-card__server-errors"
            >
              <ul>
                {serverFieldErrors.map((error, index) => (
                  <li key={`${error.fieldLabel}-${index}`}>
                    <strong>{error.fieldLabel}:</strong> {error.message}
                  </li>
                ))}
              </ul>
            </BannerAlert>
          </div>
        )}
        {periodsError && (
          <Typography role="alert" className="forage-cycle-card__periods-error">
            {periodsError}
          </Typography>
        )}

        {periods.length === 0 ? (
          <div className="forage-cycle-empty-period">
            <Typography className="forage-cycle-empty-period__message">
              {t("forageCycle:cycle-empty-period")}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              data-testid={`add-first-period-${cycle.IdForragem}`}
              disabled={saving}
              onClick={onAddPeriod}
              className="forage-cycle-empty-period__action"
            >
              + {t("forageCycle:cycle-add-first-period")}
            </Button>
          </div>
        ) : (
          <>
            <div className="forage-cycle-period-scroll">
              <div className="forage-cycle-period-list">
                {periods.map((period, index) => (
                  <ForageCyclePeriodRow
                    cycle={cycle}
                    index={index}
                    key={
                      period.IdPeriodoCicloForragemFazenda ||
                      `novo-${cycle.IdForragem}-${index}`
                    }
                    period={period}
                    periodCount={periods.length}
                    saving={saving}
                    validationErrors={rowErrors}
                    onChange={(field, value) =>
                      onChangePeriod(index, field, value)
                    }
                    onRemove={() => onRemovePeriod(index)}
                  />
                ))}
              </div>
            </div>

            {isLegacy && (
              <div role="alert">
                <BannerAlert
                  type="warning"
                  className="forage-cycle-legacy-warning"
                >
                  {t("forageCycle:cycle-legacy-warning", {
                    count: periods.length,
                  })}
                </BannerAlert>
              </div>
            )}
            {periods.length === 4 && !isLegacy && (
              <BannerAlert type="warning" className="forage-cycle-period-limit">
                {t("forageCycle:cycle-period-limit")}
              </BannerAlert>
            )}
            <div className="forage-cycle-add-action">
              <Button
                color="primary"
                variant="outlined"
                data-testid={`add-period-${cycle.IdForragem}`}
                disabled={saving || isLegacy || periods.length >= 4}
                onClick={onAddPeriod}
              >
                + {t("forageCycle:cycle-add-period", { count: periods.length })}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ForageCycleCard;
